import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Descriptions, Empty, Input, Modal, Spin, message } from 'antd'

const UPDATE_CUSTOMER_VOICE_NOTE_MUTATION = gql`
  mutation UpdateCustomerVoiceNote($id: ID!, $note: String) {
    updateCustomerVoiceNote(id: $id, note: $note) {
      saved {
        id
        note
      }
      errors {
        key
        message
      }
    }
  }
`

const formatErrors = (errors) => errors.map(({ key, message: text }) => (key ? `${key}: ${text}` : text)).join(', ')

const NoteModal = ({ open, loading, customerVoice, onClose }) => {
  const { t } = useTranslation('customerVoices')
  const [note, setNote] = useState('')
  const [updateNote, { loading: saving }] = useMutation(UPDATE_CUSTOMER_VOICE_NOTE_MUTATION, {
    refetchQueries: ['CustomerVoices']
  })

  useEffect(() => {
    setNote((customerVoice && customerVoice.note) || '')
  }, [customerVoice])

  const handleSave = async () => {
    try {
      const { data } = await updateNote({ variables: { id: customerVoice.id, note } })
      const payload = data && data.updateCustomerVoiceNote
      if (payload && payload.errors && payload.errors.length) {
        message.error(formatErrors(payload.errors))
        return
      }
      message.success(t('noteSaved'))
      onClose()
    } catch (error) {
      message.error(error.message)
    }
  }

  const renderBody = () => {
    if (loading) return <Spin />
    if (!customerVoice) return <Empty description={t('notFound')} />

    return (
      <>
        <Descriptions column={1} size='small' bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('sender')}>
            {customerVoice.name || (customerVoice.user && customerVoice.user.name) || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('description')}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{customerVoice.description}</div>
          </Descriptions.Item>
        </Descriptions>
        <Input.TextArea
          rows={5}
          value={note}
          placeholder={t('notePlaceholder')}
          onChange={(event) => setNote(event.target.value)}
        />
      </>
    )
  }

  return (
    <Modal
      open={open}
      title={t('editNote')}
      okText={t('save')}
      cancelText={t('cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: !customerVoice }}
      onOk={handleSave}
      onCancel={onClose}
      destroyOnClose
    >
      {renderBody()}
    </Modal>
  )
}

NoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  customerVoice: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default NoteModal
